/**
 * Lightweight behavioral analytics helper.
 *
 * Fire-and-forget: every call is best-effort and MUST NEVER block the UI or
 * throw into the caller. If the network fails, if the backend is down, if
 * cookies are disabled — we silently swallow.
 *
 * Backend endpoint: POST /api/events/track
 * The backend rejects unknown event names (allow-list). Keep this list in
 * sync with backend/event_api.py EVENT_NAMES.
 */
import api from '@/lib/api';
import { toast } from 'sonner';

/* --------------------------------------------------------------------------
   Shared "Quick Win completed!" toast
   --------------------------------------------------------------------------
   Fired from JD Analyzer / Role Explorer / Network In / ProfileContext when
   a Quick Win auto-completes. Kept in one place so the timing + copy + the
   "Go to My Progress" action stay consistent everywhere.
   -------------------------------------------------------------------------- */
export function showQuickWinToast(description, opts = {}) {
  try {
    toast.success('Quick Win completed!', {
      description,
      // Extended dwell (~13.5s total, up from the previous 3.5s) so users on
      // slower pages have time to notice + click the action.
      duration: 13500,
      // The action button lives on the toast so users can jump directly to
      // My Progress and see the completion state without leaving the page
      // they're on manually.
      action: {
        label: 'View My Progress',
        onClick: () => {
          try {
            trackEvent(EVENTS.deeplink_clicked, { destination: '/dashboard/progress' });
          } catch (_e) { /* no-op */ }
          if (typeof window !== 'undefined') {
            window.location.assign('/dashboard/progress');
          }
        },
      },
      ...opts,
    });
  } catch (_e) { /* no-op — toast is optional */ }
}

// Session-scoped debounce keys so we never spam the same event repeatedly in
// one page load (e.g. `dashboard_first_view` should fire once per session).
const _sessionFired = new Set();

// Client-side allow-list mirrors backend EVENT_NAMES exactly. Kept as an
// object so tree-shaking + editor autocomplete work when we reference names.
export const EVENTS = {
  signup: 'signup',
  pdf_uploaded: 'pdf_uploaded',
  analysis_completed: 'analysis_completed',
  dashboard_first_view: 'dashboard_first_view',
  assessment_rerun: 'assessment_rerun',
  jd_analysis_run: 'jd_analysis_run',
  role_card_expanded: 'role_card_expanded',
  resource_opened: 'resource_opened',
  task_expanded: 'task_expanded',
  task_marked_done: 'task_marked_done',
  deeplink_clicked: 'deeplink_clicked',
};

/**
 * Send a behavioral event. Silent on failure.
 * @param {string} event_name — one of EVENTS.*
 * @param {object} [properties] — small primitive-only key/values
 * @param {object} [opts]
 * @param {boolean} [opts.oncePerSession=false] — de-dupe within this session
 */
export function trackEvent(event_name, properties = {}, opts = {}) {
  if (!event_name || typeof event_name !== 'string') return;
  if (opts.oncePerSession) {
    const key = `${event_name}::${JSON.stringify(properties)}`;
    if (_sessionFired.has(key)) return;
    _sessionFired.add(key);
  }
  // Trim properties to primitives only — never send free text objects.
  const clean = {};
  for (const [k, v] of Object.entries(properties || {})) {
    if (typeof k !== 'string' || k.length > 60) continue;
    if (typeof v === 'string') clean[k] = v.slice(0, 200);
    else if (typeof v === 'number' || typeof v === 'boolean') clean[k] = v;
  }
  // Fire without awaiting; ignore errors entirely.
  try {
    // eslint-disable-next-line no-void
    void api.post('/events/track', { event_name, properties: clean }).catch(() => {});
  } catch (_e) { /* no-op */ }
}

/**
 * Convenience: fire a deep-link event when the user clicks a cross-tab link.
 * The receiving page can also fire its own event; this one records intent.
 */
export function trackDeeplink(destination) {
  trackEvent(EVENTS.deeplink_clicked, { destination: String(destination || '').slice(0, 60) });
}
